import { useEffect } from 'react';

import Button from '../../generic/Button';
import LabeledTextInput from '../../generic/Forms/LabeledTextInput';
import ForgotPasswordLink from '../components/ForgotPasswordLink';
import SocialLoginButtons from '../components/SocialLoginButtons';
import useResolveUser from '../hooks/useResolveUser';
import type { OAuthProvider, ResolveUserResponse } from '../../../../shared/types/routes/auth/v2';

import styles from '../Login.module.less';

export type EnterIdentifierStepProps = {
  identifier: string;
  onIdentifierChange: (value: string) => void;
  oauthProviders: OAuthProvider[];
  onResolved: (response: ResolveUserResponse) => void;
};

const EnterIdentifierStep = ({
  identifier,
  onIdentifierChange,
  oauthProviders,
  onResolved,
}: EnterIdentifierStepProps) => {
  const resolveUser = useResolveUser();

  // Advance the flow from an effect once resolution lands, rather than the mutate `onSuccess` callback. React Query
  // runs that callback before notifying its subscribers, so navigating there unmounts this step while React Query is
  // still about to update it — which warns about a state update on an unmounted component under React 17. Reacting
  // to the settled `data` here defers the transition until after this step has re-rendered, sidestepping the race.
  useEffect(() => {
    if (resolveUser.data) {
      onResolved(resolveUser.data);
    }
  }, [resolveUser.data, onResolved]);

  const handleSubmit = () => {
    if (!identifier || resolveUser.isLoading) {
      return;
    }
    resolveUser.mutate({ identifier, context: 'login' });
  };

  return (
    <form
      className={styles.card}
      onSubmit={event => {
        event.preventDefault();
        handleSubmit();
      }}
    >
      <h1 className={styles.heading}>Sign In</h1>
      <div className={styles.fieldGroup}>
        <LabeledTextInput
          label="Email or username"
          // type="text" rather than "email" so the browser does not reject a bare username.
          type="text"
          name="identifier"
          placeholder="Enter your username or email"
          value={identifier}
          autoFocus
          isRequired
          onChange={onIdentifierChange}
        />
        <ForgotPasswordLink />
      </div>
      {/* onClick, not a submit button: Enter submits through the form's onSubmit, and a non-submit button
          avoids a double-fire while still working under jsdom, which doesn't dispatch native submit on click. */}
      <Button isFullWidth size="large" isDisabled={resolveUser.isLoading} onClick={handleSubmit}>
        Next
      </Button>
      <SocialLoginButtons providers={oauthProviders} />
    </form>
  );
};

export default EnterIdentifierStep;
